import { memo, useEffect, useMemo, useRef } from 'react'
import { AlertTriangle, CheckCircle2, Radio, RotateCw, Server } from 'lucide-react'
import { currentAndNext, formatClock, formatDuration, isClockProgramme, itemTitle, nextLiveState } from '../../lib/clock.js'
import type { Channel, ClockLayoutConfig, ClockTimePrecision, ClockWidget, ScheduleItem } from '../../types/domain.js'
import { useStreamWatchdog } from '../../hooks/useStreamWatchdog.js'
import { useFitText } from '../../hooks/useFitText.js'
import { resolveClockFontCqw } from '../../lib/clockFontScale.js'
import { liveBlockReference, liveBlockRundown } from '../../lib/livePartPlan.js'

interface Props {
  config: ClockLayoutConfig
  channel: Channel
  items: ScheduleItem[]
  playout: Record<string, unknown> | null
  nowMs: number
  selectedId?: string | null
  editing?: boolean
  simulation?: boolean
  onSelect?: (id: string) => void
  onPointerDown?: (event: React.PointerEvent, widget: ClockWidget, mode: 'move' | 'resize') => void
  onKeyDown?: (event: React.KeyboardEvent, widget: ClockWidget) => void
}

export const ClockCanvas = memo(function ClockCanvas(props: Props) {
  return (
    <div className="clock-canvas" data-editing={props.editing || undefined} role={props.editing ? 'application' : undefined} aria-label={props.editing ? 'Toile Clock 1920 par 1080' : undefined}>
      {props.editing && <div className="clock-canvas__safe" aria-hidden="true" />}
      {props.config.widgets.map(widget => widget.visible && (
        <div
          key={widget.id}
          className="clock-canvas__widget"
          data-selected={props.selectedId === widget.id || undefined}
          data-surface={widget.style.surface}
          data-color={widget.style.colorRole}
          data-align={widget.style.align}
          data-weight={widget.style.weight}
          data-type={widget.type}
          data-overflow={widget.style.overflow ?? 'SHRINK'}
          style={{
            left: `${widget.x / 19.2}%`, top: `${widget.y / 10.8}%`,
            width: `${widget.width / 19.2}%`, height: `${widget.height / 10.8}%`,
            zIndex: props.config.widgets.indexOf(widget) + 1,
            // Tailles réglées par l'opérateur, converties en `cqw` de toile —
            // l'unité de `clock.css`. Voir `lib/clockFontScale.ts`.
            ...cssFontVars(widget),
          } as React.CSSProperties}
          tabIndex={props.editing ? 0 : undefined}
          onClick={() => props.onSelect?.(widget.id)}
          onPointerDown={event => props.onPointerDown?.(event, widget, 'move')}
          onKeyDown={event => props.onKeyDown?.(event, widget)}
        >
          <ClockWidgetContent {...props} widget={widget} />
          {props.editing && !widget.locked && (
            <button className="clock-canvas__resize" type="button" aria-label={`Redimensionner ${widget.name}`} onPointerDown={event => { event.stopPropagation(); props.onPointerDown?.(event, widget, 'resize') }} />
          )}
        </div>
      ))}
    </div>
  )
})

const ClockWidgetContent = memo(function ClockWidgetContent({ widget, channel, items, playout, nowMs, simulation }: Props & { widget: ClockWidget }) {
  const { current, nextProgram } = useMemo(() => currentAndNext(items, nowMs), [items, nowMs])
  const live = useMemo(() => nextLiveState(items, nowMs, widget.config), [items, nowMs, widget.config])
  // Déroulé des parties : le bloc qui porte l'antenne prime sur « le prochain
  // direct ». Pendant un décrochage sans durée fixe, aucune reprise n'est encore
  // posée et `nextLiveState` désignerait une émission sans rapport. Calculé pour
  // le seul widget concerné : la toile en compte une dizaine et se redessine à
  // chaque dixième de seconde.
  const partRundown = useMemo(() => {
    if (widget.type !== 'LIVE_PART') return null
    const reference = liveBlockReference(items, nowMs) ?? live.item
    return reference ? liveBlockRundown(reference, items, nowMs) : null
  }, [widget.type, items, nowMs, live.item])
  const label = widget.config.label ?? widget.name
  const currentProgram = current && isClockProgramme(current) ? current : null
  const featuredProgram = currentProgram ?? nextProgram
  const programIsUpcoming = !currentProgram && Boolean(nextProgram)
  const start = currentProgram ? new Date(currentProgram.startTime).getTime() : 0
  const end = currentProgram ? new Date(currentProgram.endTime).getTime() : 0
  const actualStart = currentProgram?.liveGoLiveAt ?? currentProgram?.asRunLog?.actualStart ?? currentProgram?.startTime
  const actualStartMs = actualStart ? new Date(actualStart).getTime() : null
  const topDelta = actualStartMs != null && currentProgram ? actualStartMs - start : null
  const remainingTarget = currentProgram ? end : nextProgram ? new Date(nextProgram.startTime).getTime() : null
  const progress = currentProgram ? Math.max(0, Math.min(100, ((nowMs - start) / Math.max(1, end - start)) * 100)) : 0
  const showLabel = widget.style.showLabel

  const shell = (value: React.ReactNode, detail?: React.ReactNode, displayedLabel = label) => <>
    {showLabel && <div className="clock-widget__label">{displayedLabel}</div>}
    <ClockValue widget={widget}>{value}</ClockValue>
    {detail && <div className="clock-widget__detail">{detail}</div>}
  </>

  switch (widget.type) {
    case 'CLOCK': return shell(<RuntimeTimeValue nowMs={nowMs} kind="CLOCK" precision={widget.config.timePrecision ?? (widget.config.tenthsBelowSec === 10 ? 'TENTHS' : 'SECONDS')} />, `SYNCHRO SERVEUR`)
    case 'DATE': return shell(new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }).format(nowMs))
    case 'ON_AIR': return shell(
      itemTitle(featuredProgram),
      currentProgram
        ? `${currentProgram.itemType} · FIN ${formatClock(end).slice(0, 8)}`
        : nextProgram
          ? <>DÉBUT {formatClock(new Date(nextProgram.startTime).getTime()).slice(0, 8)}{current ? ` · ACTUELLEMENT ${itemTitle(current).toUpperCase()}` : ''}</>
          : current ? `AUCUN PROGRAMME À VENIR · ACTUELLEMENT ${itemTitle(current).toUpperCase()}` : 'AUCUN PROGRAMME À L’ANTENNE',
      programIsUpcoming ? (widget.config.label ?? 'Prochain programme') : label,
    )
    case 'REMAINING': return shell(
      remainingTarget == null ? '—' : <RuntimeTimeValue nowMs={nowMs} kind="COUNTDOWN" targetMs={remainingTarget} precision={widget.config.timePrecision} tenthsBelowSec={widget.config.tenthsBelowSec ?? 10} />,
      undefined,
      programIsUpcoming ? (widget.config.label ?? 'Programme dans') : label,
    )
    case 'ELAPSED': return shell(actualStartMs == null ? '—' : <RuntimeTimeValue nowMs={nowMs} kind="ELAPSED" targetMs={actualStartMs} precision={widget.config.timePrecision} tenthsBelowSec={widget.config.tenthsBelowSec} />)
    case 'NEXT_ITEM': return shell(itemTitle(nextProgram), nextProgram ? <>DANS <RuntimeTimeValue nowMs={nowMs} kind="COUNTDOWN" targetMs={new Date(nextProgram.startTime).getTime()} precision={widget.config.timePrecision} /></> : 'FIN DE CONDUCTEUR')
    case 'NEXT_LIVE': return <div className="clock-live" data-severity={live.severity}>
      <div className="clock-widget__eyebrow">{live.severity === 'warning' || live.severity === 'danger' ? <AlertTriangle /> : <Radio />} {live.label}</div>
      <div className="clock-widget__value">{live.counterMs == null || !live.item ? '—' : <RuntimeTimeValue
        nowMs={nowMs}
        kind={live.phase === 'OVERRUN' || live.phase === 'WAITING' ? 'ELAPSED' : 'COUNTDOWN'}
        targetMs={new Date(live.phase === 'OVERRUN' ? live.item.endTime : live.phase === 'WAITING' ? live.item.startTime : live.phase === 'LIVE' || live.phase === 'ENDING_SOON' || live.phase === 'ENDING_IMMINENT' ? live.item.endTime : live.item.startTime).getTime()}
        precision={widget.config.timePrecision}
        tenthsBelowSec={widget.config.tenthsBelowSec ?? 10}
      />}</div>
      <div className="clock-widget__title">{itemTitle(live.item)}</div>
      <div className="clock-widget__detail">{live.item ? `TOP ${formatClock(new Date(live.item.startTime).getTime()).slice(0, 8)}${live.theoreticalEnd ? ' · FIN THÉORIQUE' : ''}` : 'Aucun direct planifié'}</div>
    </div>
    case 'TOP': return shell(topDelta == null ? '—' : formatDuration(topDelta, { signed: true, precision: widget.config.timePrecision, tenthsBelowSec: 10 }), actualStart ? `RÉEL ${formatClock(new Date(actualStart).getTime()).slice(0, 8)}` : 'TOP NON REÇU')
    // Partie en cours du déroulé d'un bloc de direct. Seules les durées sont
    // estimées : chaque partie est calée sur son segment de direct, donc son
    // compteur repart au raccrochage et s'efface pendant le décrochage. Le
    // repère ne commande ni coupure ni alerte, et son compteur peut atteindre
    // zéro sans que rien ne se passe à l'antenne.
    case 'LIVE_PART': {
      const part = partRundown?.cursor ?? null
      if (!part || !part.partCount) return shell('—', 'AUCUN DÉROULÉ ESTIMÉ')
      // Pendant la coupure il n'y a rien à décompter : le compteur de la partie
      // suivante ne repart qu'au raccrochage.
      const target = part.remainingMs == null
        ? null
        : part.phase === 'PART' ? part.current?.endMs : part.next?.startMs
      const heading = part.phase === 'BREAK'
        ? `COUPURE · RETOUR PARTIE ${part.next?.number ?? ''}`.trim()
        : part.phase === 'AFTER'
          ? 'DÉROULÉ TERMINÉ'
          : `PARTIE ${(part.phase === 'PART' ? part.current?.number : part.next?.number) ?? ''} / ${part.partCount}`
      const partTitle = (part.phase === 'PART' ? part.current?.title : part.next?.title) ?? ''
      return shell(
        target == null
          ? '—'
          : <RuntimeTimeValue nowMs={nowMs} kind="COUNTDOWN" targetMs={target} precision={widget.config.timePrecision} tenthsBelowSec={widget.config.tenthsBelowSec} />,
        `${heading}${partTitle ? ` · ${partTitle.toUpperCase()}` : ''} · ESTIMÉ`,
      )
    }
    case 'LIVE_END': return shell(live.item ? <RuntimeTimeValue nowMs={nowMs} kind="COUNTDOWN" targetMs={new Date(live.item.endTime).getTime()} precision={widget.config.timePrecision} /> : '—', live.theoreticalEnd ? 'FIN THÉORIQUE' : 'FIN PRÉVUE')
    case 'PROGRESS': return <>{showLabel && <div className="clock-widget__label">{label}</div>}<div className="clock-progress"><span style={{ width: `${progress}%` }} /></div><div className="clock-widget__detail">{Math.round(progress)} %</div></>
    case 'RUNDOWN': return <div className="clock-rundown">{items.filter(item => new Date(item.startTime).getTime() >= nowMs).slice(0, widget.config.itemCount ?? 4).map(item => <div key={item.id}><time>{formatClock(new Date(item.startTime).getTime()).slice(0, 5)}</time><span>{itemTitle(item)}</span></div>)}</div>
    case 'LIVE_SOURCE': return shell(current?.liveSource?.status === 'ACTIVE' ? <><CheckCircle2 /> SOURCE ACTIVE</> : <><AlertTriangle /> SOURCE INDISPONIBLE</>, current?.liveSource?.name)
    case 'SYSTEM_STATUS': return <div className="clock-statuses"><span><Server /> SERVEUR OK</span><span><Radio /> WS OK</span><span>{playout?.bridgeConnected ? <CheckCircle2 /> : <AlertTriangle />} CASPARCG {playout?.bridgeConnected ? 'OK' : 'INDISPONIBLE'}</span></div>
    case 'ALERTS': return shell(<><AlertTriangle /> AUCUNE ALERTE ACTIVE</>)
    case 'TEXT': return shell(widget.config.text ?? 'Texte libre')
    case 'CHANNEL_IDENTITY': return shell(channel.name, channel.slug?.toUpperCase())
    case 'VIDEO_RETURN': return <ClockVideoReturn />
    default: return shell(simulation ? 'SIMULATION' : '—')
  }
})

interface RuntimeTimeValueProps {
  nowMs: number
  kind: 'CLOCK' | 'COUNTDOWN' | 'ELAPSED'
  targetMs?: number
  precision?: ClockTimePrecision
  tenthsBelowSec?: number
}

const RuntimeTimeValue = memo(function RuntimeTimeValue({ nowMs, kind, targetMs = 0, precision, tenthsBelowSec }: RuntimeTimeValueProps) {
  const valueRef = useRef<HTMLSpanElement>(null)
  const anchorRef = useRef({ serverMs: nowMs, monotonicAt: performance.now() })

  useEffect(() => {
    anchorRef.current = { serverMs: nowMs, monotonicAt: performance.now() }
  }, [nowMs])

  useEffect(() => {
    let frame = 0
    const update = () => {
      const currentMs = anchorRef.current.serverMs + performance.now() - anchorRef.current.monotonicAt
      const value = kind === 'CLOCK'
        ? formatClock(currentMs, precision)
        : formatDuration(kind === 'COUNTDOWN' ? targetMs - currentMs : currentMs - targetMs, { precision, tenthsBelowSec })
      if (valueRef.current && valueRef.current.textContent !== value) valueRef.current.textContent = value
      frame = requestAnimationFrame(update)
    }
    update()
    return () => cancelAnimationFrame(frame)
  }, [kind, precision, targetMs, tenthsBelowSec])

  const initialValue = kind === 'CLOCK'
    ? formatClock(nowMs, precision)
    : formatDuration(kind === 'COUNTDOWN' ? targetMs - nowMs : nowMs - targetMs, { precision, tenthsBelowSec })
  return <span ref={valueRef}>{initialValue}</span>
})

const ClockVideoReturn = memo(function ClockVideoReturn() {
  const { playerKey, iframeRef, live, reload } = useStreamWatchdog()
  const transport = window.location.protocol === 'https:' ? 'hls' : 'webrtc'
  return <div className="clock-video-return">
    <iframe key={playerKey} ref={iframeRef} src={`/stream/${transport}/antenna/?controls=false&muted=true&autoplay=true&playsInline=true`} title="Retour antenne Clock" allow="autoplay" />
    <div className="clock-video-return__status"><span>{live ? 'RETOUR LIVE' : 'CONNEXION'}</span><button type="button" onClick={event => { event.stopPropagation(); reload() }} aria-label="Recharger le retour antenne"><RotateCw /></button></div>
  </div>
})

/** Variables CSS de taille, consommées par `clock.css`. */
function cssFontVars(widget: ClockWidget): Record<string, string> {
  const font = resolveClockFontCqw(widget)
  return {
    '--clock-value-size': `${font.value}cqw`,
    '--clock-label-size': `${font.label}cqw`,
    '--clock-title-size': `${font.title}cqw`,
  }
}

/**
 * Valeur principale d'un widget. En mode `SHRINK` — le défaut — la taille réglée
 * est un plafond : un contenu trop long est réduit plutôt que tronqué.
 */
const ClockValue = memo(function ClockValue({ widget, children }: { widget: ClockWidget; children: React.ReactNode }) {
  const font = resolveClockFontCqw(widget)
  const mode = widget.style.overflow ?? 'SHRINK'
  const ref = useFitText<HTMLDivElement>({
    maxFontCqw: font.value,
    enabled: mode === 'SHRINK',
    contentKey: typeof children === 'string' || typeof children === 'number' ? children : widget.type,
  })
  return <div ref={ref} className="clock-widget__value">{children}</div>
})
